import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiDollarSign, FiPlus, FiSearch, FiRefreshCw, 
  FiDownload, FiTrash2, FiEye 
} from 'react-icons/fi';
import { saleAPI } from '../../services/api';
import { toast } from 'react-toastify';
import SaleFormModal from './SaleFormModal';

const NATURE_CONFIG = {
  direct_sale: { label: 'Vente Directe', color: '#166534', bg: '#dcfce7' },
  resale: { label: 'Revente', color: '#7c3aed', bg: '#ede9fe' },
  purchase_for_client: { label: 'Achat Client', color: '#1e40af', bg: '#dbeafe' },
  vefa_off_plan: { label: 'Vente sur Plan (VEFA)', color: '#c2410c', bg: '#ffedd5' },
};

const STATUS_CONFIG = {
  prospect: { label: 'Prospect / Négociation', color: '#64748b', bg: '#f1f5f9' },
  offer_made: { label: 'Offre Déposée', color: '#d97706', bg: '#fef3c7' },
  offer_accepted: { label: 'Offre Acceptée', color: '#2563eb', bg: '#dbeafe' },
  compromis_signed: { label: 'Compromis Signé', color: '#7c3aed', bg: '#ede9fe' },
  conditions_cleared: { label: 'Conditions Levées', color: '#0284c7', bg: '#e0f2fe' },
  deed_signed: { label: 'Acte Notarié Signé', color: '#059669', bg: '#d1fae5' },
  completed: { label: 'Clôturée / Vendue', color: '#16a34a', bg: '#dcfce7' },
  cancelled: { label: 'Annulée', color: '#dc2626', bg: '#fee2e2' },
};

const SalesListPage = () => {
  const [sales, setSales] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [natureFilter, setNatureFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(null);

  const navigate = useNavigate();

  const fetchSales = useCallback(async () => {
    setLoading(true);
    try {
      const [salesRes, statsRes] = await Promise.all([
        saleAPI.list({
          transaction_nature: natureFilter !== 'all' ? natureFilter : undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          search: searchTerm || undefined,
        }),
        saleAPI.getStats(),
      ]);
      setSales(Array.isArray(salesRes.data) ? salesRes.data : []);
      setStats(statsRes.data || {});
    } catch (err) {
      toast.error('Erreur chargement des transactions de vente');
    } finally {
      setLoading(false);
    }
  }, [natureFilter, statusFilter, searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSales();
    }, 200);
    return () => clearTimeout(timer);
  }, [fetchSales]);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce dossier de vente ?')) return;
    try {
      await saleAPI.delete(id);
      toast.success('Dossier supprimé avec succès');
      fetchSales();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleDownloadPDF = async (sale, e) => {
    e.stopPropagation();
    setDownloadingPDF(sale.id);
    try {
      const res = await saleAPI.downloadCompromisPDF(sale.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Compromis-Vente-${sale.sale_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Compromis PDF téléchargé avec succès');
    } catch (e) {
      toast.error('Erreur lors du téléchargement du PDF');
    } finally {
      setDownloadingPDF(null);
    }
  };

  return (
    <div className="page-container" style={{ paddingBottom: 40 }}>
      {/* ── Header ── */}
      <div className="page-header">
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiDollarSign color="var(--primary, #2563eb)" /> Ventes, Achats & Reventes Immobilières
          </h1>
          <p style={{ color: 'var(--text-secondary, #64748b)', margin: '6px 0 0', fontSize: 13 }}>
            Pilotage des transactions de terrains (TF, Concession), villas, immeubles, offres d'achat et actes notariés.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button 
            className="btn btn-secondary btn-sm" 
            onClick={fetchSales} 
            disabled={loading}
            title="Actualiser la liste"
          >
            <FiRefreshCw className={loading ? 'spin' : ''} /> Actualiser
          </button>
          <button 
            className="btn btn-primary btn-sm" 
            onClick={() => setIsModalOpen(true)}
          >
            <FiPlus /> Nouvelle Transaction
          </button>
        </div>
      </div>

      {/* ── Metric Cards ── */}
      <div className="grid-4" style={{ marginBottom: 24 }}>
        <div className="card" style={{ padding: '14px 18px', borderRadius: 10 }}>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase' }}>Total Dossiers</div>
          <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{stats.total_sales || sales.length}</div>
        </div>
        <div className="card" style={{ padding: '14px 18px', borderRadius: 10 }}>
          <div style={{ fontSize: 11, color: '#d97706', fontWeight: 600, textTransform: 'uppercase' }}>En Négociation / Cours</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#d97706', marginTop: 4 }}>{stats.in_progress_sales || 0}</div>
        </div>
        <div className="card" style={{ padding: '14px 18px', borderRadius: 10 }}>
          <div style={{ fontSize: 11, color: '#16a34a', fontWeight: 600, textTransform: 'uppercase' }}>Ventes Clôturées</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#16a34a', marginTop: 4 }}>{stats.completed_sales || 0}</div>
        </div>
        <div className="card" style={{ padding: '14px 18px', borderRadius: 10 }}>
          <div style={{ fontSize: 11, color: '#7c3aed', fontWeight: 600, textTransform: 'uppercase' }}>Reventes & Plus-values</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#7c3aed', marginTop: 4 }}>
            {stats.resales_count || 0}
            {stats.total_capital_gain > 0 && (
              <span style={{ fontSize: 11, fontWeight: 500, marginLeft: 6, color: '#16a34a' }}>
                (+{parseFloat(stats.total_capital_gain).toLocaleString('fr-FR')} XOF)
              </span>
            )}
          </div>
        </div>
        <div className="card" style={{ padding: '14px 18px', borderRadius: 10, background: '#f8fafc', border: '1px solid #cbd5e1' }}>
          <div style={{ fontSize: 11, color: '#1e40af', fontWeight: 600, textTransform: 'uppercase' }}>Commissions Agence</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#1e40af', marginTop: 4 }}>
            {parseFloat(stats.total_commission_earned || 0).toLocaleString('fr-FR')} XOF
          </div>
        </div>
      </div>

      {/* ── Filters & Search ── */}
      <div className="card" style={{ padding: '14px 18px', marginBottom: 20, borderRadius: 10 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Search box */}
          <div style={{ position: 'relative', flex: 1, minWidth: 240 }}>
            <FiSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              type="text"
              className="form-control"
              style={{ paddingLeft: 36, fontSize: 13 }}
              placeholder="Rechercher par n° vente, bien, TF, vendeur, acheteur ou notaire..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Nature filter */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>Nature :</span>
            <select
              className="form-control"
              style={{ width: 'auto', padding: '6px 10px', fontSize: 13 }}
              value={natureFilter}
              onChange={(e) => setNatureFilter(e.target.value)}
            >
              <option value="all">Toutes les opérations</option>
              <option value="direct_sale">Ventes Directes</option>
              <option value="resale">Reventes</option>
              <option value="purchase_for_client">Achats Client</option>
              <option value="vefa_off_plan">VEFA (Sur Plan)</option>
            </select>
          </div>

          {/* Status filter */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>Statut :</span>
            <select
              className="form-control"
              style={{ width: 'auto', padding: '6px 10px', fontSize: 13 }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Tous les statuts</option>
              <option value="prospect">Prospect / Négociation</option>
              <option value="offer_made">Offre Déposée</option>
              <option value="offer_accepted">Offre Acceptée</option>
              <option value="compromis_signed">Compromis Signé</option>
              <option value="conditions_cleared">Conditions Levées</option>
              <option value="deed_signed">Acte Notarié Signé</option>
              <option value="completed">Clôturées</option>
              <option value="cancelled">Annulées</option>
            </select>
          </div>
        </div>
      </div>

      {/* ── Table Container avec Défilement Horizontal Fluide ── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div className="spinner" style={{ margin: '0 auto 16px' }} />
          <p style={{ color: 'var(--text-secondary)' }}>Chargement des transactions...</p>
        </div>
      ) : sales.length === 0 ? (
        <div className="card" style={{ padding: '60px 20px', textAlign: 'center', borderRadius: 10 }}>
          <FiDollarSign size={48} style={{ color: '#cbd5e1', marginBottom: 16 }} />
          <h3 style={{ margin: '0 0 8px', color: '#1e293b' }}>Aucune transaction trouvée</h3>
          <p style={{ color: 'var(--text-secondary)', maxWidth: 450, margin: '0 auto 20px', fontSize: 14 }}>
            {searchTerm || natureFilter !== 'all' || statusFilter !== 'all'
              ? 'Aucun dossier ne correspond à vos critères de filtre.'
              : 'Enregistrez votre première opération d\'achat, vente ou revente.'}
          </p>
          <button className="btn btn-primary" onClick={() => setIsModalOpen(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <FiPlus /> Nouvelle Transaction
          </button>
        </div>
      ) : (
        <div className="card" style={{ borderRadius: 10, overflow: 'hidden' }}>
          <div className="table-container">
            <table style={{ width: '100%', minWidth: 880, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg, #f8fafc)', borderBottom: '2px solid var(--border, #e2e8f0)' }}>
                  <th style={thStyle}>Réf. & Nature</th>
                  <th style={thStyle}>Bien & Foncier</th>
                  <th style={thStyle}>Parties (Vendeur / Acheteur)</th>
                  <th style={thStyle}>Prix & Commission</th>
                  <th style={thStyle}>Offres & Pièces</th>
                  <th style={thStyle}>Statut</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sales.map((sale) => {
                  const natureInfo = NATURE_CONFIG[sale.transaction_nature] || { label: sale.transaction_nature, color: '#334155', bg: '#f1f5f9' };
                  const statusInfo = STATUS_CONFIG[sale.status] || { label: sale.status, color: '#64748b', bg: '#f1f5f9' };
                  const price = sale.agreed_sale_price || sale.listed_price;

                  return (
                    <tr
                      key={sale.id}
                      onClick={() => navigate(`/sales/${sale.id}`)}
                      style={{ borderBottom: '1px solid var(--border, #e2e8f0)', cursor: 'pointer', transition: 'background 0.15s' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg, #f8fafc)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      {/* Réf & Nature */}
                      <td style={tdStyle}>
                        <div style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--primary, #2563eb)', fontSize: 13 }}>
                          {sale.sale_number}
                        </div>
                        <div style={{ marginTop: 4 }}>
                          <span style={{
                            padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                            background: natureInfo.bg, color: natureInfo.color,
                          }}>
                            {natureInfo.label}
                          </span>
                        </div>
                      </td>

                      {/* Bien & Foncier */}
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: '#0f172a' }}>
                          {sale.property_title}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary, #64748b)', marginTop: 2 }}>
                          {sale.property_neighborhood ? `${sale.property_neighborhood}, ` : ''}{sale.property_city || 'Bamako'}
                          {sale.land_area ? ` · ${sale.land_area}m²` : ''}
                        </div>
                        {sale.land_title_number && (
                          <div style={{ fontSize: 10, color: '#7c3aed', fontWeight: 600, marginTop: 2 }}>
                            📜 {sale.land_title_type ? sale.land_title_type.toUpperCase() : 'TF'} : {sale.land_title_number}
                          </div>
                        )}
                      </td>

                      {/* Parties */}
                      <td style={tdStyle}>
                        <div style={{ fontSize: 12 }}>
                          <span style={{ color: 'var(--text-secondary)' }}>Vend : </span>
                          <strong>{sale.seller_first_name ? `${sale.seller_first_name} ${sale.seller_last_name}` : '—'}</strong>
                        </div>
                        <div style={{ fontSize: 12, marginTop: 2 }}>
                          <span style={{ color: 'var(--text-secondary)' }}>Ach : </span>
                          {sale.buyer_first_name ? (
                            <strong style={{ color: '#16a34a' }}>{sale.buyer_first_name} {sale.buyer_last_name}</strong>
                          ) : (
                            <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>En recherche</span>
                          )}
                        </div>
                      </td>

                      {/* Prix & Commission */}
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>
                          {parseFloat(price || 0).toLocaleString('fr-FR')} XOF
                        </div>
                        <div style={{ fontSize: 11, color: '#2563eb', fontWeight: 500, marginTop: 2 }}>
                          Com. ({sale.agency_commission_rate || 5}%) : {parseFloat(sale.agency_commission_amount || 0).toLocaleString('fr-FR')} XOF
                        </div>
                        {sale.transaction_nature === 'resale' && sale.capital_gain > 0 && (
                          <div style={{ fontSize: 10, color: '#16a34a', fontWeight: 600, marginTop: 1 }}>
                            Plus-value : +{parseFloat(sale.capital_gain).toLocaleString('fr-FR')} XOF
                          </div>
                        )}
                      </td>

                      {/* Offres & Pièces */}
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <span style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, fontSize: 11, color: '#334155', fontWeight: 600 }}>
                            💼 {sale.offers_count || 0}
                          </span>
                          <span style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, fontSize: 11, color: '#334155', fontWeight: 600 }}>
                            📁 {sale.documents_count || 0}
                          </span>
                        </div>
                      </td>

                      {/* Statut */}
                      <td style={tdStyle}>
                        <span
                          style={{
                            padding: '4px 10px',
                            borderRadius: 12,
                            fontSize: 11,
                            fontWeight: 600,
                            background: statusInfo.bg,
                            color: statusInfo.color,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {statusInfo.label}
                        </span>
                      </td>

                      {/* Actions */}
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end' }}>
                          <button
                            type="button"
                            title="Voir le dossier"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/sales/${sale.id}`);
                            }}
                            style={actionBtn('var(--primary, #2563eb)')}
                          >
                            <FiEye size={14} />
                          </button>
                          <button
                            type="button"
                            title="Télécharger Compromis de Vente PDF"
                            disabled={downloadingPDF === sale.id}
                            onClick={(e) => handleDownloadPDF(sale, e)}
                            style={actionBtn('#059669')}
                          >
                            <FiDownload size={14} />
                          </button>
                          <button
                            type="button"
                            title="Supprimer la transaction"
                            onClick={(e) => handleDelete(sale.id, e)}
                            style={actionBtn('#dc2626')}
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal création */}
      <SaleFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={fetchSales}
      />
    </div>
  );
};

const thStyle = {
  padding: '12px 16px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--text-secondary, #64748b)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  whiteSpace: 'nowrap'
};
const tdStyle = { padding: '12px 16px', fontSize: 13, verticalAlign: 'middle' };
const actionBtn = (color) => ({
  width: 32, height: 32, border: 'none', borderRadius: 6, cursor: 'pointer',
  background: color + '15', color: color, fontSize: 14,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  transition: 'background 0.2s', flexShrink: 0
});

export default SalesListPage;
